import { IPaxTypeQuantity } from "@common/models/PaxTypeQuantity";
import { onFlightSearch } from "@components/flightSearch/FlightSearch";
import { IViewport } from "@config/appConfig";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Notification from "@service/Notification";
import Translate from "@service/Translate";
import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import Button from "./form/Button";
import { IFormControlProps } from "./form/Form";
import RangeInput from "./form/RangeInput";
import Icon from "./Icon";
import Modal from "./Modal";

interface IPaxType extends IFormControlProps {
    paxType: IPaxTypeQuantity;
}

const PaxType: FC<IPaxType> = ({ name, paxType, label, error, disabled, onChange }) => {
    const { tr } = Translate;
    const { small } = Config.get<IViewport>("viewport");
    const { availabilityRequest, width } = useSelector((state: IAppState) => ({
        availabilityRequest: state.availabilityRequest,
        width: state.windowWidth
    }));
    const paxTypeRef = useRef<HTMLDivElement>(null);
    const { push } = useHistory();
    const [showModal, setShowModal] = useState<boolean>(false);
    const passCount = paxType?.adt + paxType?.chd + paxType?.inf;
    const calcValue = passCount + " " + Translate.tr("passenger");

    useEffect(() => {
        document.addEventListener("click", handleOuterClick);

        return () => {
            document.removeEventListener("click", handleOuterClick);
        };
    }, []);

    const onRangeInputChange = (paxName: string, paxValue: any) => {
        const pax: IPaxTypeQuantity = Object.assign({}, paxType);
        pax[paxName as keyof IPaxTypeQuantity] = +paxValue;
        if (validatePax(pax, paxName))
            if (onChange && name)
                onChange(name, pax);
    }

    const handleOuterClick = (e: any) => {
        if (!paxTypeRef.current?.contains(e.target))
            if (e.target.parentElement?.id !== "paxType") {
                setShowModal(false);
            }
    }

    const onPaxTypeBtnClick = () => {
        if (width <= small)
            push(onFlightSearch(availabilityRequest))
        setShowModal(false);
    }

    return (
        <div ref={paxTypeRef} className="pax-type">
            <div className={`form-group has-icon${error ? " has-error" : ""}`}>
                {label ? <label htmlFor={name}>{label}</label> : null}
                <div className="form-container">
                    <Icon name="icon-users-outline icon-input" />
                    <input
                        id={name}
                        className={`form-control${showModal ? " form-focused" : ""}`}
                        name={name}
                        onClick={() => setShowModal(true)}
                        onFocus={() => setShowModal(true)}
                        value={calcValue}
                        readOnly={true}
                        disabled={disabled}
                    />
                </div>
            </div>
            <Modal
                id="paxType"
                show={showModal}
                name={width < small ? "slide-up" : "fade"}
                mode={width < small ? "full" : "inline"}
                onClose={() => setShowModal(false)}
            >
                <div className="pax-type-container">
                    <div className="pax-type-header">
                        {tr("select_passengers_count")}
                    </div>
                    <div className="pax-type-item">
                        <div className="pax-title">
                            <div className="pax-label">{tr("adt")}</div>
                            <div className="pax-desc">{tr("age_years_old", "+12")}</div>
                        </div>
                        <div className="pax-value">
                            <RangeInput
                                name="adt"
                                min={1}
                                value={paxType?.adt}
                                onChange={onRangeInputChange}
                            />
                        </div>
                    </div>
                    <div className="pax-type-item">
                        <div className="pax-title">
                            <div className="pax-label">{tr("chd")}</div>
                            <div className="pax-desc">{tr("age_years_old", "2-12")}</div>
                        </div>
                        <div className="pax-value">
                            <RangeInput
                                name="chd"
                                min={0}
                                value={paxType?.chd}
                                onChange={onRangeInputChange}
                            />
                        </div>
                    </div>
                    <div className="pax-type-item infant">
                        <div className="pax-title">
                            <div className="pax-label">{tr("inf")}</div>
                            <div className="pax-desc">{tr("age_under_year", "2")}</div>
                        </div>
                        <div className="pax-value">
                            <RangeInput
                                name="inf"
                                min={0}
                                value={paxType?.inf}
                                onChange={onRangeInputChange}
                            />
                        </div>
                    </div>
                    <Button
                        variant="contained"
                        text={width > small ? tr("btn_confirm") : tr("btn_search_and_confirm")}
                        className="pax-type-accept-btn"
                        height="48px"
                        color="primary"
                        onClick={onPaxTypeBtnClick}
                    />
                </div>
            </Modal>
        </div>
    )
}

export default PaxType;

export function validatePax(pax: IPaxTypeQuantity, type: string) {
    let valid = false;
    const { tr } = Translate;
    if (!pax.adt) return false;
    if (pax.adt + pax.chd + pax.inf > 9) {
        Notification.error(tr("err_additional_passenger_number"));
    } else {
        if (pax.inf <= pax.adt)
            valid = true;
        else
            Notification.error(tr("err_additional_infant_number"));
    }
    return valid;
}